import { useCallback, useState } from 'react'

import { useSession } from '@/features/auth/api/useSession'

/* Whether this reader keeps the record dock open (0052).
 *
 * ONE PREFERENCE, NOT ONE PER RECORD. Somebody who minimises the chat is
 * saying something about how they are working this afternoon — reading a
 * costing, checking a money table — not something about AB-27 in particular. A
 * per-record memory would mean a reader who collapsed the dock on one case
 * meets it expanded again on the next, which is the same surprise the
 * preference exists to remove.
 *
 * IT LIVES IN localStorage, for the reasons `components/data/useViewState`
 * already argues at length: this is a workstation habit rather than a profile
 * setting, there is no preferences endpoint, and adding one is a backend story
 * with its own migration. The key shape is that module's, deliberately —
 *
 *   asa:chat:<userId>:dock
 *
 * — INCLUDING THE USER ID. Two people share the workstation in the Türkmenbaşy
 * office, and without the id the second one inherits the first one's dock. That
 * matters more here than it does for a column preference: the dock is where a
 * colleague's conversation is, and "the chat was already minimised so I did not
 * look" is precisely the failure this surface exists to prevent. While the
 * session is still in flight the id is `anon`, and the entry is re-read once the
 * real id lands.
 *
 * THE DEFAULT IS EXPANDED, and that is load-bearing rather than arbitrary. It
 * is what carries FR-46/UX-DR-13's intent across the change that gave the dock
 * a close button: a reader who never touches the control gets exactly the old
 * always-visible behaviour, and the conversation becomes hidden only by a
 * deliberate act of the person who then knows it is hidden. Every failure path
 * below — storage disabled, a garbage value, a quota refusal — falls back to
 * expanded for the same reason. The dock must never be silent by accident.
 *
 * DELIBERATELY NOT SYNCED ACROSS TABS. A `storage` listener would make a second
 * tab follow the first, and the ordinary two-tab shape here is one record being
 * read beside another — collapsing the dock to see one case's money table is
 * not a statement about the case open next door.
 */

/** The id used until the session answers, matching `useViewState`'s. Not a
 *  reserved-looking value: an entry under `anon` is a legitimate thing to find
 *  on a machine nobody has signed in on yet. */
const ANON = 'anon'

type DockState = 'expanded' | 'collapsed'

const DEFAULT: DockState = 'expanded'

/** `asa:chat:<userId>:dock`. Exported so a test names the entry the way the
 *  code does rather than by repeating the string and drifting from it. */
export function chatDockKey(userId: string): string {
  return `asa:chat:${userId}:dock`
}

/* Stored as JSON, like every other entry under the `asa:` prefix, so one reader
   and one writer serve them all. `"collapsed"` is still legible to anyone
   looking at storage in devtools. */
function read(userId: string): DockState {
  let stored: string | null
  try {
    stored = localStorage.getItem(chatDockKey(userId))
  } catch {
    // Storage disabled entirely — a private window, a locked-down policy. The
    // dock still works, it just cannot remember anything, and the thing it
    // forgets is a minimise rather than an open.
    return DEFAULT
  }
  if (stored === null) return DEFAULT
  try {
    const raw: unknown = JSON.parse(stored)
    // localStorage is user-writable and outlives the code that wrote it, so
    // the value is validated rather than trusted. Anything unrecognised is a
    // reader who has not chosen, which means expanded.
    return raw === 'collapsed' || raw === 'expanded' ? raw : DEFAULT
  } catch {
    return DEFAULT
  }
}

function write(userId: string, value: DockState): void {
  try {
    localStorage.setItem(chatDockKey(userId), JSON.stringify(value))
  } catch {
    // A full quota or a blocked store must not take the interaction with it:
    // the reader's click still collapses the dock, it just is not remembered.
  }
}

export interface ChatDockState {
  /** True when the reader has minimised the dock to its floating button. */
  collapsed: boolean
  setCollapsed: (next: boolean) => void
}

/**
 * The reader's collapsed/expanded choice, remembered across navigation and
 * reload. Desktop only in effect — below `lg` the dock is a full-screen view
 * the page opens and closes, and this preference says nothing about it.
 */
export function useChatDockState(): ChatDockState {
  const { data: user } = useSession()
  const userId = user?.id ?? ANON

  const [state, setState] = useState<DockState>(() => read(userId))

  /* Re-read when the entry's identity changes — the session resolving from
     `anon` to a real id. Done DURING RENDER rather than in an effect, the
     pattern `useViewState` and `useFormDraft` document: an effect would paint
     one frame of the previous reader's choice before correcting it, and the
     frame that must not be shown to the wrong person is the one where somebody
     else's dock is already minimised. */
  const [lastUserId, setLastUserId] = useState(userId)
  if (userId !== lastUserId) {
    setLastUserId(userId)
    setState(read(userId))
  }

  const setCollapsed = useCallback(
    (next: boolean) => {
      const value: DockState = next ? 'collapsed' : 'expanded'
      write(userId, value)
      setState(value)
    },
    [userId],
  )

  return { collapsed: state === 'collapsed', setCollapsed }
}
